import type { LocalDate } from '../dates';
import type { Mutation } from '../mutations';
import { isValidLocalDate } from '../budget/schedule';
import { MAX_AMOUNT_MINOR } from '../spending/expense';
import type { BuildCtx } from '../spending/types';
import {
  paymentsMutation,
  type InstallmentLike,
  type PayeeKind,
  type PayeeLike,
  type PaymentsResult,
  type Repeat,
} from './types';

export const MAX_PAYEE_NAME = 40;
export const MAX_PAYEE_NOTE = 200;
export const MAX_PAYMENTS = 600;
/** 100% a month is surely a typo. */
export const MAX_INTEREST_BP = 10_000;
/** "Due N days after the statement": 1 to 60 days. */
export const MAX_DUE_DAYS_AFTER = 60;

/** What the payee form edits: every field except the ones the app sets. */
export type PayeeDraft = Omit<PayeeLike, 'id' | 'trackFrom' | 'deletedAt'>;

export type PayeeError =
  | 'name_required'
  | 'name_too_long'
  | 'amount_required'
  | 'bad_amount'
  | 'bad_day'
  | 'bad_date'
  | 'bad_every'
  | 'end_before_start'
  | 'bad_count'
  | 'statement_day_required'
  | 'bad_days_after'
  | 'lender_required'
  | 'count_or_end_required'
  | 'principal_required'
  | 'bad_rate'
  | 'bad_last4'
  | 'note_too_long';

const okAmount = (n: number | null): boolean =>
  n === null || (Number.isInteger(n) && n >= 0 && n <= MAX_AMOUNT_MINOR);
const okDay = (n: number) => Number.isInteger(n) && n >= 1 && n <= 31;
const clean = (s: string | null) => {
  const t = (s ?? '').trim().replace(/\s+/g, ' ');
  return t === '' ? null : t;
};

/** A blank draft of a kind (the add form starts from it). */
export function blankPayee(kind: PayeeKind, today: LocalDate, remindDays = 3): PayeeDraft {
  return {
    kind,
    name: '',
    categoryId: null,
    fundId: null,
    amountMinor: null,
    repeat: 'monthly',
    everyN: 1,
    dueDays: [Number(today.slice(8, 10))],
    startDate: today,
    endDate: null,
    paymentCount: null,
    autoPay: false,
    remindDays,
    issuer: null,
    last4: null,
    statementDay: null,
    dueDaysAfterStatement: null,
    creditLimitMinor: null,
    openingBalanceMinor: null,
    cardholder: null,
    lender: null,
    loanType: kind === 'loan' ? 'fixed' : null,
    principalMinor: null,
    interestBp: null,
    note: null,
  };
}

/** Clears the fields a kind doesn't use, trims text, and keeps due days sorted. */
export function normalizePayee(d: PayeeDraft): PayeeDraft {
  const card = d.kind === 'card';
  const loan = d.kind === 'loan';
  const flexible = loan && d.loanType === 'flexible';
  const repeat: Repeat = card ? 'monthly' : d.repeat;
  return {
    ...d,
    name: clean(d.name) ?? '',
    amountMinor: card ? null : d.amountMinor,
    repeat,
    everyN: card ? 1 : Math.max(1, d.everyN || 1),
    dueDays: [...new Set(d.dueDays)].sort((a, b) => a - b).slice(0, card ? 1 : 2),
    endDate: card || flexible ? null : d.endDate,
    paymentCount: card || flexible ? null : d.paymentCount,
    issuer: card ? clean(d.issuer) : null,
    last4: card ? clean(d.last4) : null,
    statementDay: card ? d.statementDay : null,
    dueDaysAfterStatement: card ? (d.dueDaysAfterStatement ?? null) : null,
    creditLimitMinor: card ? d.creditLimitMinor : null,
    openingBalanceMinor: card ? d.openingBalanceMinor : null,
    cardholder: card ? clean(d.cardholder) : null,
    lender: loan ? clean(d.lender) : null,
    loanType: loan ? (d.loanType ?? 'fixed') : null,
    principalMinor: loan ? d.principalMinor : null,
    interestBp: flexible ? d.interestBp : null,
    autoPay: flexible ? false : d.autoPay,
    note: clean(d.note),
  };
}

/** Required fields per kind (spec §5, §7: e.g. a loan needs a lender and a due day). */
export function validatePayee(raw: PayeeDraft): PayeeError | null {
  const d = normalizePayee(raw);
  if (d.name === '') return 'name_required';
  if (d.name.length > MAX_PAYEE_NAME) return 'name_too_long';
  if (d.note && d.note.length > MAX_PAYEE_NOTE) return 'note_too_long';
  for (const n of [d.amountMinor, d.creditLimitMinor, d.openingBalanceMinor, d.principalMinor])
    if (!okAmount(n)) return 'bad_amount';
  if (!isValidLocalDate(d.startDate)) return 'bad_date';
  if (d.endDate !== null && !isValidLocalDate(d.endDate)) return 'bad_date';
  if (d.endDate !== null && d.endDate < d.startDate) return 'end_before_start';
  if (
    d.paymentCount !== null &&
    !(Number.isInteger(d.paymentCount) && d.paymentCount >= 1 && d.paymentCount <= MAX_PAYMENTS)
  )
    return 'bad_count';
  if (!Number.isInteger(d.everyN) || d.everyN < 1 || d.everyN > 24) return 'bad_every';
  const dueAfterStatement = d.kind === 'card' && d.dueDaysAfterStatement != null;
  if (
    !dueAfterStatement &&
    d.repeat !== 'weekly' &&
    d.repeat !== 'none' &&
    (d.dueDays.length === 0 || !d.dueDays.every(okDay))
  )
    return 'bad_day';
  switch (d.kind) {
    case 'bill':
    case 'utility':
      if (!d.amountMinor) return 'amount_required';
      return null;
    case 'card':
      if (d.statementDay === null || !okDay(d.statementDay)) return 'statement_day_required';
      if (
        d.dueDaysAfterStatement != null &&
        !(
          Number.isInteger(d.dueDaysAfterStatement) &&
          d.dueDaysAfterStatement >= 1 &&
          d.dueDaysAfterStatement <= MAX_DUE_DAYS_AFTER
        )
      )
        return 'bad_days_after';
      if (d.last4 !== null && !/^\d{4}$/.test(d.last4)) return 'bad_last4';
      return null;
    case 'loan':
      if (!d.lender) return 'lender_required';
      if (d.loanType === 'flexible') {
        if (!d.principalMinor) return 'principal_required';
        if (
          d.interestBp !== null &&
          !(Number.isInteger(d.interestBp) && d.interestBp >= 0 && d.interestBp <= MAX_INTEREST_BP)
        )
          return 'bad_rate';
        return null;
      }
      if (!d.amountMinor) return 'amount_required';
      if (d.paymentCount === null && d.endDate === null) return 'count_or_end_required';
      return null;
  }
}

function values(d: PayeeDraft): Record<string, unknown> {
  return {
    kind: d.kind,
    name: d.name,
    categoryId: d.categoryId,
    fundId: d.fundId,
    ...(d.accountId !== undefined ? { accountId: d.accountId } : {}),
    amountMinor: d.amountMinor,
    currency: 'PHP',
    repeat: d.repeat,
    everyN: d.everyN,
    dueDays: d.dueDays,
    startDate: d.startDate,
    endDate: d.endDate,
    paymentCount: d.paymentCount,
    autoPay: d.autoPay,
    remindDays: d.remindDays,
    issuer: d.issuer,
    last4: d.last4,
    statementDay: d.statementDay,
    dueDaysAfterStatement: d.dueDaysAfterStatement ?? null,
    creditLimitMinor: d.creditLimitMinor,
    openingBalanceMinor: d.openingBalanceMinor,
    cardholder: d.cardholder,
    lender: d.lender,
    loanType: d.loanType,
    principalMinor: d.principalMinor,
    interestBp: d.interestBp,
    note: d.note,
  };
}

/** Add: trackFrom = today, so dues scheduled before today count as paid (OPEN-QUESTIONS v0.5 Q6). */
export function buildAddPayee(
  draft: PayeeDraft,
  today: LocalDate,
  ctx: BuildCtx,
): PaymentsResult<{ id: string; mutation: Mutation }, PayeeError> {
  const error = validatePayee(draft);
  if (error) return { ok: false, error };
  const id = ctx.newId();
  const d = normalizePayee(draft);
  return {
    ok: true,
    id,
    mutation: paymentsMutation(ctx, 'payees', 'insert', id, { ...values(d), trackFrom: today }),
  };
}

/** Edit: only the fields that changed (the kind never changes). */
export function buildEditPayee(
  current: PayeeLike,
  draft: PayeeDraft,
  ctx: BuildCtx,
): PaymentsResult<{ mutation: Mutation | null }, PayeeError> {
  const error = validatePayee({ ...draft, kind: current.kind });
  if (error) return { ok: false, error };
  const next = values(normalizePayee({ ...draft, kind: current.kind }));
  const before = values(current);
  const changed: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(next)) {
    if (JSON.stringify(v) !== JSON.stringify(before[k])) changed[k] = v;
  }
  if (Object.keys(changed).length === 0) return { ok: true, mutation: null };
  return { ok: true, mutation: paymentsMutation(ctx, 'payees', 'update', current.id, changed) };
}

/** Soft delete; a card's installments go with it. Paid expenses stay in Spending. */
export function buildDeletePayee(
  payee: Pick<PayeeLike, 'id'>,
  installments: readonly Pick<InstallmentLike, 'id' | 'cardId' | 'deletedAt'>[],
  ctx: BuildCtx,
): Mutation[] {
  return [
    paymentsMutation(ctx, 'payees', 'delete', payee.id),
    ...installments
      .filter((i) => i.cardId === payee.id && i.deletedAt === null)
      .map((i) => paymentsMutation(ctx, 'installments', 'delete', i.id)),
  ];
}

/** Undo of a delete: plain inserts with no values restore the rows (conventions §14). */
export function buildRestorePayee(
  payeeId: string,
  installmentIds: readonly string[],
  ctx: BuildCtx,
): Mutation[] {
  return [
    paymentsMutation(ctx, 'payees', 'insert', payeeId, {}),
    ...installmentIds.map((id) => paymentsMutation(ctx, 'installments', 'insert', id, {})),
  ];
}

// ---------------------------------------------------------------------------------------------
// Installments

export type InstallmentDraft = Pick<
  InstallmentLike,
  'item' | 'monthlyMinor' | 'months' | 'startMonth' | 'categoryId' | 'note'
>;

export type InstallmentError =
  'item_required' | 'name_too_long' | 'bad_amount' | 'bad_months' | 'bad_month';

export function validateInstallment(d: InstallmentDraft): InstallmentError | null {
  const item = clean(d.item);
  if (!item) return 'item_required';
  if (item.length > MAX_PAYEE_NAME) return 'name_too_long';
  if (!(
    Number.isInteger(d.monthlyMinor) &&
    d.monthlyMinor > 0 &&
    d.monthlyMinor <= MAX_AMOUNT_MINOR
  ))
    return 'bad_amount';
  if (!(Number.isInteger(d.months) && d.months >= 1 && d.months <= 120)) return 'bad_months';
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(d.startMonth)) return 'bad_month';
  return null;
}

const installmentValues = (cardId: string, d: InstallmentDraft) => ({
  cardId,
  item: clean(d.item),
  monthlyMinor: d.monthlyMinor,
  months: d.months,
  startMonth: d.startMonth,
  categoryId: d.categoryId,
  currency: 'PHP',
  note: clean(d.note),
});

export function buildAddInstallment(
  cardId: string,
  draft: InstallmentDraft,
  ctx: BuildCtx,
): PaymentsResult<{ id: string; mutation: Mutation }, InstallmentError> {
  const error = validateInstallment(draft);
  if (error) return { ok: false, error };
  const id = ctx.newId();
  return {
    ok: true,
    id,
    mutation: paymentsMutation(ctx, 'installments', 'insert', id, installmentValues(cardId, draft)),
  };
}

export function buildEditInstallment(
  current: InstallmentLike,
  draft: InstallmentDraft,
  ctx: BuildCtx,
): PaymentsResult<{ mutation: Mutation | null }, InstallmentError> {
  const error = validateInstallment(draft);
  if (error) return { ok: false, error };
  const next: Record<string, unknown> = installmentValues(current.cardId, draft);
  const before: Record<string, unknown> = installmentValues(current.cardId, current);
  const changed: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(next)) if (v !== before[k]) changed[k] = v;
  if (Object.keys(changed).length === 0) return { ok: true, mutation: null };
  return {
    ok: true,
    mutation: paymentsMutation(ctx, 'installments', 'update', current.id, changed),
  };
}
